import { ReportData } from '../types/reportTypes';
import { codySalesJulyData } from './codySalesJulyData';
import { codySalesAugustData } from './codySalesAugustData';
import { codySalesSeptemberData } from './codySalesSeptemberData';

// Helper function to sum arrays of numbers
const sum = (numbers: number[]): number => {
  return numbers.reduce((total, num) => total + num, 0);
};

// Combine all job data from Q3 months
const allJobData = [
  ...codySalesJulyData.jobData,
  ...codySalesAugustData.jobData,
  ...codySalesSeptemberData.jobData
];

// Calculate combined sales by source
const salesBySource = (() => {
  const sourceMap = new Map<string, any>();

  // Combine data from all three months
  const allSalesBySource = [
    ...codySalesJulyData.salesBySource,
    ...codySalesAugustData.salesBySource,
    ...codySalesSeptemberData.salesBySource
  ];

  allSalesBySource.forEach(source => {
    const key = source.source;
    if (!sourceMap.has(key)) {
      sourceMap.set(key, {
        source: key,
        count: 0,
        totalSales: 0,
        grossProfit: 0
      });
    }

    const sourceData = sourceMap.get(key);
    sourceData.count += source.count;
    sourceData.totalSales += source.totalSales;
    sourceData.grossProfit += source.grossProfit;
  });

  return Array.from(sourceMap.values());
})();

// Calculate combined ad spend by source (all should be 0 for direct sales)
const adSpendBySource = (() => {
  const uniqueSources = new Set<string>();
  allJobData.forEach(job => uniqueSources.add(job.leadSource));

  return Array.from(uniqueSources).map(source => ({
    source: source,
    spend: 0
  }));
})();

// Calculate combined sales by trade
const salesByTrade = (() => {
  const tradeMap = new Map<string, any>();

  // Process all jobs to get trade totals
  allJobData.forEach(job => {
    const key = job.trade;

    if (!tradeMap.has(key)) {
      tradeMap.set(key, {
        trade: key,
        count: 0,
        totalSales: 0,
        grossProfit: 0,
        avgContractValue: 0,
        avgGrossMargin: 0
      });
    }

    const tradeData = tradeMap.get(key);
    tradeData.count += 1;
    tradeData.totalSales += job.contractTotal;
    tradeData.grossProfit += job.grossProfit;
  });

  // Calculate averages
  tradeMap.forEach(trade => {
    trade.avgContractValue = trade.count > 0 ? trade.totalSales / trade.count : 0;
    trade.avgGrossMargin = trade.totalSales > 0 ? trade.grossProfit / trade.totalSales : 0;
  });

  return Array.from(tradeMap.values());
})();

// Calculate Q3 totals by combining all three months
const q3Totals = {
  adSpend: codySalesJulyData.kpis.adSpend + codySalesAugustData.kpis.adSpend + codySalesSeptemberData.kpis.adSpend,
  totalSales: codySalesJulyData.kpis.totalSales + codySalesAugustData.kpis.totalSales + codySalesSeptemberData.kpis.totalSales,
  grossProfit: codySalesJulyData.kpis.grossProfit + codySalesAugustData.kpis.grossProfit + codySalesSeptemberData.kpis.grossProfit,
  salesCommissions: codySalesJulyData.kpis.salesCommissions + codySalesAugustData.kpis.salesCommissions + codySalesSeptemberData.kpis.salesCommissions,
  managerFees: codySalesJulyData.kpis.managerFees + codySalesAugustData.kpis.managerFees + codySalesSeptemberData.kpis.managerFees,
  companyOH: codySalesJulyData.kpis.companyOH + codySalesAugustData.kpis.companyOH + codySalesSeptemberData.kpis.companyOH,
  netProfit: codySalesJulyData.kpis.netProfit + codySalesAugustData.kpis.netProfit + codySalesSeptemberData.kpis.netProfit
};

// Export the consolidated Q3 2025 report data
export const codyQ3Data: ReportData = {
  period: {
    month: 9, // Using September as the end month of Q3
    year: 2025,
    quarter: 3,
    startMonth: 7,
    endMonth: 9
  },
  kpis: {
    adSpend: q3Totals.adSpend,
    totalSales: q3Totals.totalSales,
    grossProfit: q3Totals.grossProfit,
    salesCommissions: q3Totals.salesCommissions,
    managerFees: q3Totals.managerFees,
    companyOH: q3Totals.companyOH,
    netProfit: q3Totals.netProfit,
    netProfitPercent: q3Totals.totalSales > 0 ? q3Totals.netProfit / q3Totals.totalSales : 0,
    grossProfitPercent: q3Totals.totalSales > 0 ? q3Totals.grossProfit / q3Totals.totalSales : 0
  },
  salesBySource,
  salesByTrade,
  adSpendBySource,
  jobData: allJobData
};

// Export monthly breakdown separately for the Q3 component
export const codyQ3MonthlyBreakdown = [
  {
    month: 'July',
    totalSales: codySalesJulyData.kpis.totalSales,
    grossProfit: codySalesJulyData.kpis.grossProfit,
    netProfit: codySalesJulyData.kpis.netProfit,
    jobCount: codySalesJulyData.jobData.length
  },
  {
    month: 'August',
    totalSales: codySalesAugustData.kpis.totalSales,
    grossProfit: codySalesAugustData.kpis.grossProfit,
    netProfit: codySalesAugustData.kpis.netProfit,
    jobCount: codySalesAugustData.jobData.length
  },
  {
    month: 'September',
    totalSales: codySalesSeptemberData.kpis.totalSales,
    grossProfit: codySalesSeptemberData.kpis.grossProfit,
    netProfit: codySalesSeptemberData.kpis.netProfit,
    jobCount: codySalesSeptemberData.jobData.length
  }
];
